import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ConfidenceIndicator from "./ConfidenceIndicator";
import OcrFeedbackButtons from "./OcrFeedbackButtons";

interface PatientInfo {
  first_name: string;
  last_name: string;
  dob: string;
  gender?: string | null;
  phone?: string | null;
  insurance_carrier?: string | null;
}

interface Test {
  test_type: string;
  test_description: string;
  test_reason?: string;
  notes?: string;
  evidence?: string;
  body_part?: string | null;
  laterality?: string | null;
  contrast?: string | null;
  icd_code?: string | null;
}

interface Confidence {
  overall: number;
  physician_name: number;
  patient_info: number;
  tests: number;
}

interface ExtractedData {
  physician_name: string | null;
  physician_title?: string | null;
  signature: boolean;
  patient_info: PatientInfo;
  order_date: string;
  tests: Test[];
  results_explanation?: string;
  confidence: Confidence;
  warnings: string[];
  document_type?: string;
  facility_name?: string | null;
  physician_npi?: string | null;
  physician_phone?: string | null;
  icd_codes?: string[];
  additional_info?: string[];
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  imaging_order: "Imaging Order",
  prescription: "Prescription",
  electronic_referral: "Electronic Referral",
  fax_referral: "Fax Referral",
  other: "Other",
};

interface OcrResult {
  resultId: string;
  fileId: string;
  inviteId: string;
  extractedData?: ExtractedData;
  status: "processing" | "completed" | "failed" | "verified" | "corrected";
  confidenceScore?: number;
  feedback?: {
    rating: "up" | "down";
    timestamp: string;
    userId: string;
  };
  correctedData?: ExtractedData;
  correctionComments?: string;
  errorMessage?: string;
}

interface OcrResultsPanelProps {
  /**
   * OCR result to display
   */
  result: OcrResult;
  /**
   * Callback when user approves (thumbs up) the OCR result
   */
  onApprove?: () => void;
  /**
   * Callback when user rejects (thumbs down) the OCR result
   */
  onReject?: () => void;
  /**
   * Callback when user wants to edit/correct the OCR result
   */
  onEdit?: () => void;
  /**
   * Whether feedback is being submitted
   */
  loading?: boolean;
}

/**
 * Displays OCR extraction results with confidence indicators and feedback buttons
 * Shows physician info, patient info, tests, and allows user to approve/reject/edit
 */
function OcrResultsPanel({
  result,
  onApprove,
  onReject,
  onEdit,
  loading = false,
}: OcrResultsPanelProps) {
  const { status, extractedData, correctedData, errorMessage, feedback } =
    result;

  // Use corrected data if available, otherwise use extracted data
  const displayData = correctedData || extractedData;

  // Render processing state
  if (status === "processing") {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 4,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body1" color="text.secondary">
              Processing document with AI...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render failed state
  if (status === "failed") {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              OCR Processing Failed
            </Typography>
            <Typography variant="body2">
              {errorMessage || "An error occurred while processing the document."}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Render results (completed, verified, or corrected)
  if (!displayData) {
    return null;
  }

  const { confidence, warnings } = displayData;
  const sectionContainerSx = {
    mb: 2.5,
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    bgcolor: "background.paper",
  } as const;
  const fieldLabelSx = {
    color: "text.secondary",
    fontWeight: 600,
    mr: 0.5,
  } as const;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header with status and overall confidence */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              OCR Results
            </Typography>
            {displayData.document_type && (
              <Chip
                label={DOCUMENT_TYPE_LABELS[displayData.document_type] || displayData.document_type}
                size="small"
                variant="outlined"
              />
            )}
            {status === "verified" && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Verified"
                color="success"
                size="small"
              />
            )}
            {status === "corrected" && (
              <Chip
                icon={<EditIcon />}
                label="Corrected"
                color="info"
                size="small"
              />
            )}
          </Box>
          <ConfidenceIndicator
            score={confidence.overall}
            label="Overall Confidence"
            size="medium"
          />
        </Box>

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Warnings:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {warnings.map((warning, index) => (
                <li key={index}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Physician Information */}
        <Box sx={sectionContainerSx}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            Physician Information
            <ConfidenceIndicator score={confidence.physician_name} />
          </Typography>
          <Stack spacing={1}>
            {displayData.facility_name && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Facility:
                </Box>
                {displayData.facility_name}
              </Typography>
            )}
            <Typography variant="body2">
              <Box component="span" sx={fieldLabelSx}>
                Name:
              </Box>
              {displayData.physician_name || "Not found"}
            </Typography>
            {displayData.physician_title && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Title:
                </Box>
                {displayData.physician_title}
              </Typography>
            )}
            {displayData.physician_npi && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  NPI:
                </Box>
                {displayData.physician_npi}
              </Typography>
            )}
            {displayData.physician_phone && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Phone:
                </Box>
                {displayData.physician_phone}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={fieldLabelSx}>
                Signature:
              </Typography>
              <Chip
                label={displayData.signature ? "Present" : "Not found"}
                color={displayData.signature ? "success" : "warning"}
                size="small"
              />
            </Box>
          </Stack>
        </Box>

        {/* Patient Information */}
        <Box sx={sectionContainerSx}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            Patient Information
            <ConfidenceIndicator score={confidence.patient_info} />
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              <Box component="span" sx={fieldLabelSx}>
                Name:
              </Box>
              {displayData.patient_info.first_name} {displayData.patient_info.last_name}
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={fieldLabelSx}>
                Date of Birth:
              </Box>
              {displayData.patient_info.dob}
            </Typography>
            {displayData.patient_info.gender && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Gender:
                </Box>
                {displayData.patient_info.gender}
              </Typography>
            )}
            {displayData.patient_info.phone && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Phone:
                </Box>
                {displayData.patient_info.phone}
              </Typography>
            )}
            {displayData.patient_info.insurance_carrier && (
              <Typography variant="body2">
                <Box component="span" sx={fieldLabelSx}>
                  Insurance:
                </Box>
                {displayData.patient_info.insurance_carrier}
              </Typography>
            )}
            <Typography variant="body2">
              <Box component="span" sx={fieldLabelSx}>
                Order Date:
              </Box>
              {displayData.order_date}
            </Typography>
          </Stack>
        </Box>

        {/* ICD Codes */}
        {displayData.icd_codes && displayData.icd_codes.length > 0 && (
          <Box sx={sectionContainerSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              ICD / Diagnosis Codes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {displayData.icd_codes.map((code, index) => (
                <Chip key={index} label={code} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Tests */}
        <Box sx={sectionContainerSx}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            Tests Ordered
            <ConfidenceIndicator score={confidence.tests} />
          </Typography>
          {displayData.tests && displayData.tests.length > 0 ? (
            <Stack spacing={1.5}>
              {displayData.tests.map((test, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {test.test_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {test.test_description}
                  </Typography>
                  {(test.body_part || test.laterality || test.contrast) && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                      {test.body_part && (
                        <Chip label={test.body_part} size="small" variant="outlined" />
                      )}
                      {test.laterality && (
                        <Chip label={test.laterality} size="small" variant="outlined" />
                      )}
                      {test.contrast && (
                        <Chip label={test.contrast.replace(/_/g, " ")} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}
                  {test.icd_code && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>ICD:</strong> {test.icd_code}
                    </Typography>
                  )}
                  {test.test_reason && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Reason:</strong> {test.test_reason}
                    </Typography>
                  )}
                  {test.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Notes:</strong> {test.notes}
                    </Typography>
                  )}
                  {test.evidence && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Evidence:</strong> {test.evidence}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No tests found
            </Typography>
          )}
        </Box>

        {/* Additional Information */}
        {displayData.additional_info && displayData.additional_info.length > 0 && (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Additional Notes:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {displayData.additional_info.map((info, index) => (
                <li key={index}>
                  <Typography variant="body2">{info}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Results Explanation */}
        {displayData.results_explanation && (
          <Box sx={sectionContainerSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Results Explanation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayData.results_explanation}
            </Typography>
          </Box>
        )}

        {/* Correction Comments (if corrected) */}
        {status === "corrected" && result.correctionComments && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Correction Comments:
              </Typography>
              <Typography variant="body2">{result.correctionComments}</Typography>
            </Alert>
          </>
        )}

        {/* Action Buttons */}
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <OcrFeedbackButtons
            onThumbsUp={onApprove || (() => {})}
            onThumbsDown={onReject || (() => {})}
            currentRating={feedback?.rating || null}
            loading={loading}
            disabled={!onApprove && !onReject}
          />
          {onEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
              disabled={loading}
            >
              Edit / Correct
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default OcrResultsPanel;
